import { useState, useEffect } from 'react';

import { COURSE_STATUSES } from 'containers/user/learn/containers/content/cources/course.constants';

import api from 'api';
import { getCmsCoursesMeta } from 'api/generated/user-cms-metadata-controller/user-cms-metadata-controller';
import type { CmsCourseDto } from 'api/generated/schemas/cmsCourseDto';
import type { StrapiCourseAttributes, StrapiRecord } from 'api/strapi-api';

type CourseRecord = StrapiRecord<StrapiCourseAttributes>;

export interface EnrichedCourse extends CourseRecord {
  averageProgress: number;
  status: string;
  name: string | undefined;
  route: string;
  coverDescription: string | undefined;
  coverImage: string | undefined;
  firstLessonId: string | number | undefined;
}

interface UseFetchCoursesResult {
  loading: boolean;
  courses: EnrichedCourse[];
}

const useFetchCourses = (userId: string | undefined): UseFetchCoursesResult => {
  const [loading, setLoading] = useState<boolean>(false);
  const [courses, setCourses] = useState<EnrichedCourse[]>([]);

  useEffect(() => {
    const fetchCourses = async (): Promise<void> => {
      setLoading(true);
      let coursesMetadata: CmsCourseDto[] = [];

      try {
        const coursesRes = await api.getCourses(userId as string);

        try {
          coursesMetadata = await getCmsCoursesMeta(userId as string);
        } catch (metadataError) {
          if ((metadataError as { response?: { status?: number } }).response?.status === 404) {
            console.error('No courses metadata found');
            coursesMetadata = [];
          }
        }

        const updatedCourses = coursesRes?.data?.data?.map((course) => {
          if (!course.attributes.lessons?.data?.length) {
            return null;
          }

          course.attributes.lessons.data =
            course.attributes.lessons.data.filter(
              (lesson) => lesson.attributes.publishedAt
            );

          const metadata = coursesMetadata.find(
            (meta) => meta.id === course.id
          );
          let averageProgress = 0;

          if (metadata?.lessons && metadata.lessons.length > 0) {
            const courseLessonIds = new Set(
              course.attributes.lessons?.data?.map((lesson) => lesson.id)
            );
            const filteredLessons = metadata.lessons.filter((lesson) => {
              const lessonMetaId = lesson.id;

              return lessonMetaId != null && courseLessonIds.has(lessonMetaId);
            });

            if (filteredLessons.length > 0) {
              const totalProgress = filteredLessons.reduce(
                (acc, lesson) => acc + (lesson.progress || 0),
                0
              );

              averageProgress = Math.round(
                totalProgress / (course.attributes.lessons?.data.length ?? 1)
              );
            }
          }
          const status =
            (metadata?.status &&
              COURSE_STATUSES[metadata.status as keyof typeof COURSE_STATUSES]) ||
            COURSE_STATUSES.UNKNOWN;

          const enriched: EnrichedCourse = {
            ...course,
            averageProgress,
            status: averageProgress === 100 ? COURSE_STATUSES.DONE : status,
            name: course.attributes?.name,
            route: `/learn/course/${course.id}`,
            coverDescription: course.attributes?.coverDescription,
            coverImage: course.attributes?.coverImage?.data?.attributes?.url,
            firstLessonId: course.attributes.lessons?.data[0]?.id,
          };

          return enriched;
        });

        setCourses((updatedCourses ?? []).filter(Boolean) as EnrichedCourse[]);
      } catch (error) {
        console.error('Failed to fetch courses:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchCourses();
    }
  }, [userId]);

  return { loading, courses };
};

export default useFetchCourses;
